/**
 * Hideout pieces (hideout plan P3): every template → its recipe and frame-drawn builder. A scene recipe
 * (`hush.scene.*`) composes these with existing Hush props into one hidden place.
 */
import { buildHideoutBuilding, type BuildingRecipe } from './building';
import { HIDEOUT_BUILDING_RECIPES } from './buildingRecipes';
import { buildScarecrow, SCARECROW_RECIPES, type ScarecrowRecipe } from '../scarecrow/scarecrow';
import {
  buildBramble, buildDriftwood, buildOvergrowth, buildSapling, HUSH_BRAMBLE_MOUND, HUSH_DRIFTWOOD_STACK, HUSH_HANGING_VINES,
  HUSH_IVY_RUN, HUSH_MOSS_CLUMP, HUSH_SAPLING_WILLOW,
} from './nature';
import { buildInteriorDoor, HUSH_INTERIOR_DOOR } from './interiorDoor';
import { buildTideWindow, HUSH_TIDE_WINDOW } from './tideWindow';

export const HUSH_HIDEOUT_REVISION = 'hush.hideout/revision-01';

export type HideoutPiece = import('../pieces/contract').PieceDefinition;

const building = (recipe: BuildingRecipe): HideoutPiece => ({ recipe, build: (roles, scope) => buildHideoutBuilding(recipe, roles, scope) });
const scarecrow = (recipe: ScarecrowRecipe): HideoutPiece => ({ recipe, build: (roles, scope) => buildScarecrow(recipe, roles, scope) });

export const HIDEOUT_PIECES = {
  hideout_boathouse: building(HIDEOUT_BUILDING_RECIPES.boathouse),
  hideout_woodcutter_hut: building(HIDEOUT_BUILDING_RECIPES.woodcutter_hut),
  hideout_tollhouse: building(HIDEOUT_BUILDING_RECIPES.tollhouse),
  hideout_boat_shelter: building(HIDEOUT_BUILDING_RECIPES.boat_shelter),
  // The same door from inside the Nook (P5).
  hideout_interior_door: { recipe: HUSH_INTERIOR_DOOR, build: (roles, scope) => buildInteriorDoor(HUSH_INTERIOR_DOOR, roles, scope) },
  tide_window_framed: { recipe: HUSH_TIDE_WINDOW, build: (roles, scope) => buildTideWindow(HUSH_TIDE_WINDOW, roles, scope) },
  // Masking growth and clutter: ground pieces, then overgrowth that hangs from a building's roof-edge hooks.
  willow_sapling: { recipe: HUSH_SAPLING_WILLOW, build: (roles, scope) => buildSapling(HUSH_SAPLING_WILLOW, roles, scope) },
  bramble_mass: { recipe: HUSH_BRAMBLE_MOUND, build: (roles, scope) => buildBramble(HUSH_BRAMBLE_MOUND, roles, scope) },
  driftwood_stack: { recipe: HUSH_DRIFTWOOD_STACK, build: (roles, scope) => buildDriftwood(HUSH_DRIFTWOOD_STACK, roles, scope) },
  ivy_run: { recipe: HUSH_IVY_RUN, build: (roles, scope) => buildOvergrowth('ivy_run', HUSH_IVY_RUN, roles, scope) },
  hanging_vines: { recipe: HUSH_HANGING_VINES, build: (roles, scope) => buildOvergrowth('hanging_vines', HUSH_HANGING_VINES, roles, scope) },
  moss_clump: { recipe: HUSH_MOSS_CLUMP, build: (roles, scope) => buildOvergrowth('moss_clump', HUSH_MOSS_CLUMP, roles, scope) },
  // Scarecrows (hideout plan 04): farm-field dressing, never near the Hush hideout.
  scarecrow_field: scarecrow(SCARECROW_RECIPES.field),
  scarecrow_moor: scarecrow(SCARECROW_RECIPES.moor),
  scarecrow_haunted: scarecrow(SCARECROW_RECIPES.haunted),
} as const satisfies Record<string, HideoutPiece>;
export type HideoutTemplate = keyof typeof HIDEOUT_PIECES;

export { buildHideoutBuilding, buildingHookPoints, validateBuildingRecipe, BUILDING_FRAMES, BUILDING_LAYOUTS } from './building';
export type { BuildingRecipe, BuildingVariant } from './building';
export { HIDEOUT_BUILDING_RECIPES } from './buildingRecipes';
export * from './nature';
export { buildInteriorDoor, HUSH_INTERIOR_DOOR, INTERIOR_DOOR_FRAME } from './interiorDoor';
export type { InteriorDoorRecipe } from './interiorDoor';
export { buildTideWindow, HUSH_TIDE_WINDOW, TIDE_WINDOW_FRAME } from './tideWindow';
export type { TideWindowRecipe } from './tideWindow';
export type { HideoutPieceArt, PieceRecipe, Point } from '../pieces/svg';
